// src/pages/WebsiteOverview.js - opravené načítání uptime dat

import React, {useState, useEffect} from 'react';
import {Badge, Card, Col, Row, Spinner, Alert} from "react-bootstrap";
import StatusCard from "../components/Dashboard/StatusCard";
import {Line} from 'react-chartjs-2';
import {Chart, registerables} from 'chart.js';
import {websiteService} from "../api/services/websites";

// Registrace potřebných Chart.js komponent
Chart.register(...registerables);

const WebsiteOverview = ({website, results}) => {
    const [uptimeData, setUptimeData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Načtení dat uptime při načtení komponenty
    useEffect(() => {
        const fetchUptimeData = async () => {
            if (!website || !website.id) return;

            setLoading(true);
            try {
                // Získání dat uptime monitoru za posledních 30 dní
                const {data} = await websiteService.getMonitoringResults(website.id, 'uptime', 288*2); // 2 dny

                // Pokud nemáme data, vrátíme se
                if (!data || !Array.isArray(data) || data.length === 0) {
                    setLoading(false);
                    return;
                }

                // Zpracování dat pro graf
                const dates = [];
                const values = [];

                if (!data || !Array.isArray(data) || data.length === 0) {
                    setLoading(false);
                    // Prázdný graf bez dat
                    return;
                }

                // Seřadíme data podle data a času vzestupně
                const sortedData = [...data].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

                sortedData.forEach(item => {
                    // Zkontrolujeme, že item má všechny potřebné vlastnosti
                    if (item && item.createdAt) {
                        const date = new Date(item.createdAt);
                        dates.push(date.toLocaleDateString());

                        // Bezpečné získání statusCode
                        const statusCode = item.details && item.details.status_code ? item.details.status_code : 0;
                        values.push(statusCode === 200 ? 100 : 0);
                    }
                });

                if (dates.length > 0 && values.length > 0) {
                    setUptimeData({
                        labels: dates,
                        datasets: [
                            {
                                label: 'Dostupnost (%)',
                                data: values,
                                fill: true,
                                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                                borderColor: 'rgba(75, 192, 192, 1)',
                                tension: 0.1
                            }
                        ]
                    });
                }
            } catch (error) {
                console.error("Chyba při načítání dat uptime:", error);
                setError("Nepodařilo se načíst data o dostupnosti webu.");
            } finally {
                setLoading(false);
            }
        };

        fetchUptimeData();
    }, [website]);

    // Zajistíme, že komponenta správně ošetří různé stavy dat
    if (!website) {
        return (
            <Alert variant="warning">
                <i className="fas fa-exclamation-triangle me-2"></i>
                Nebyl vybrán žádný web pro zobrazení.
            </Alert>
        );
    }

    // Kontrola zda máme vůbec nějaká data pro tento web
    if (!results && !loading && !error) {
        return (
            <Alert variant="info">
                <i className="fas fa-info-circle me-2"></i>
                Pro tento web zatím nejsou k dispozici žádná data. Zkuste spustit kontroly pomocí tlačítka "Obnovit
                data".
            </Alert>
        );
    }

    // Konfigurace grafu
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: {
                beginAtZero: true,
                max: 100,
                title: {
                    display: true,
                    text: 'Dostupnost (%)'
                }
            },
            x: {
                title: {
                    display: true,
                    text: 'Datum'
                }
            }
        },
        plugins: {
            legend: {
                display: true,
                position: 'top'
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        return `Dostupnost: ${context.parsed.y}%`;
                    }
                }
            }
        }
    };

    // Bezpečné získání dat z results
    const performanceData = results && results.performance ? results.performance : null;
    const wordpressData = results && results.wordpress ? results.wordpress : null;
    const sslData = results && results.security ? results.security : null;
    const pluginsData = wordpressData ? wordpressData.outdatedPlugins : null;

    const ttfb = performanceData?.length > 0 && performanceData[0]?.details?.metrics?.ttfb || 0;

    return (
        <div className="website-overview fade-in">
            <Row className="mb-4">
                <Col lg={3} md={6} className="mb-4">
                    {performanceData ? (
                        <StatusCard
                            title="TTFB"
                            value={`${ttfb} ms`}
                            icon="fa-tachometer-alt"
                            status={ttfb < 300 ? 'success' : ttfb < 1000 ? 'warning' : 'danger'}
                        />
                    ) : (
                        <StatusCard
                            title="TTFB"
                            value="N/A"
                            icon="fa-tachometer-alt"
                            status="secondary"
                        />
                    )}
                </Col>
                <Col lg={3} md={6} className="mb-4">
                    {website?.metaData?.wordpress_version ? (
                        <StatusCard
                            title="WordPress verze"
                            value={website?.metaData?.wordpress_version}
                            icon="fa-wordpress"
                            status={wordpressData?.isLatest ? 'success' : 'warning'}
                        />
                    ) : (
                        <StatusCard
                            title="WordPress verze"
                            value="N/A"
                            icon="fa-wordpress"
                            status="secondary"
                        />
                    )}
                </Col>
                <Col lg={3} md={6} className="mb-4">
                    {sslData ? (
                        <StatusCard
                            title="SSL"
                            value={sslData.valid ? 'Platný' : 'Neplatný'}
                            icon="fa-lock"
                            status={sslData.valid ? 'success' : 'danger'}
                        />
                    ) : (
                        <StatusCard
                            title="SSL"
                            value="N/A"
                            icon="fa-lock"
                            status="secondary"
                        />
                    )}
                </Col>
                <Col lg={3} md={6} className="mb-4">
                    {pluginsData !== null ? (
                        <StatusCard
                            title="Zastaralé pluginy"
                            value={pluginsData}
                            icon="fa-plug"
                            status={pluginsData === 0 ? 'success' : 'warning'}
                        />
                    ) : (
                        <StatusCard
                            title="Zastaralé pluginy"
                            value="N/A"
                            icon="fa-plug"
                            status="secondary"
                        />
                    )}
                </Col>
            </Row>

            <Card className="mb-4 shadow">
                <Card.Header>
                    <h5 className="mb-0">Dostupnost webu</h5>
                </Card.Header>
                <Card.Body>
                    <div style={{height: "300px"}}>
                        {loading ? (
                            <div className="d-flex justify-content-center align-items-center h-100">
                                <Spinner animation="border" role="status" variant="primary">
                                    <span className="visually-hidden">Načítání grafu...</span>
                                </Spinner>
                            </div>
                        ) : error ? (
                            <Alert variant="danger" className="m-0">
                                <i className="fas fa-exclamation-triangle me-2"></i>
                                {error}
                            </Alert>
                        ) : uptimeData && uptimeData.labels.length > 0 ? (
                            <Line data={uptimeData} options={chartOptions}/>
                        ) : (
                            <div className="text-center py-5 text-muted">
                                <i className="fas fa-chart-line fa-3x mb-3"></i>
                                <p>Pro tento web zatím nejsou k dispozici data o dostupnosti</p>
                            </div>
                        )}
                    </div>
                </Card.Body>
            </Card>

            <Card className="shadow">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Poslední problémy</h5>
                    <Badge bg="primary" pill>{results && results.issues ? results.issues.length : 0}</Badge>
                </Card.Header>
                <Card.Body className="p-0">
                    {!results || !results.issues || results.issues.length === 0 ? (
                        <div className="text-center py-4 text-muted">
                            <i className="fas fa-check-circle fa-3x mb-3 text-success"></i>
                            <p>Žádné nedávné problémy</p>
                        </div>
                    ) : (
                        <div className="list-group list-group-flush">
                            {results.issues.map((issuev, index) => (
                                issuev.details?.issues?.map((issue, index) => (
                                    (typeof issue === "string" || issue instanceof String) ? (
                                        <div className="list-group-item" key={index}>
                                            <div className="d-flex w-100 justify-content-between mb-1">
                                                <h6 className="mb-1">{issue}</h6>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="list-group-item" key={index}>
                                            <div className="d-flex w-100 justify-content-between mb-1">
                                                <h6 className="mb-1">{issue.message}</h6>
                                                <small className="text-muted">
                                                    {issuev.createdAt ? new Date(issuev.createdAt).toLocaleString() : 'Datum neznámé'}
                                                </small>
                                            </div>
                                            <Badge
                                                bg={issue.severity === 'high' ? 'danger' : issue.severity === 'medium' ? 'warning' : 'success'}
                                                pill
                                            >
                                                {issue.severity === 'high' ? 'Vysoká' : issue.severity === 'medium' ? 'Střední' : 'Nízká'} závažnost
                                            </Badge>
                                        </div>
                                    )))
                            ))}
                        </div>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default WebsiteOverview;